import { PublicationStatusSchema } from "../solo-cms/publication-status";
import { z } from "zod";

/**
 * Announcement CTA links are surfaced verbatim as an `<a href>` in the public Arena
 * modal. Restrict writes to http(s) so a CMS author can't persist a `javascript:` /
 * `data:` href (the public feed is world-readable). Empty string = "no link".
 */
const ExternalHttpUrlSchema = z.string().refine(
  (value) => {
    if (value === "") return true;
    try {
      const { protocol } = new URL(value);
      return protocol === "http:" || protocol === "https:";
    } catch {
      return false;
    }
  },
  { message: "Link must be an http(s) URL" },
);

// ====== Response DTO (read) ======

export const ArenaAnnouncementDtoSchema = z.object({
  id: z.uuid(),
  title: z.string().min(1),
  description: z.string(),
  link: z.string().nullable().optional(),
  imageFileId: z.uuid().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  targetUtms: z.array(z.string()).default([]),
  startDate: z.coerce.date().nullable().optional(),
  endDate: z.coerce.date().nullable().optional(),
  publicationStatus: PublicationStatusSchema,
  publishedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ArenaAnnouncementDto = z.infer<typeof ArenaAnnouncementDtoSchema>;

// ====== Save DTOs (write) ======

const BaseArenaAnnouncementDtoSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  link: ExternalHttpUrlSchema.nullable().optional(),
  imageFileId: z.uuid().nullable().optional(),
  targetUtms: z.array(z.string()).default([]),
  startDate: z.coerce.date().nullable().optional(),
  endDate: z.coerce.date().nullable().optional(),
  publicationStatus: PublicationStatusSchema,
});

/**
 * A start date after the end date yields an announcement that is never active, which
 * the author gets no feedback on — reject it at the boundary instead. Open-ended
 * windows (either bound null) are valid.
 */
const isValidValidityWindow = (data: { startDate?: Date | null; endDate?: Date | null }): boolean =>
  data.startDate == null || data.endDate == null || data.startDate.getTime() <= data.endDate.getTime();
const validityWindowError = { message: "Start date must be on or before the end date", path: ["endDate"] };

export const CreateArenaAnnouncementDtoSchema = BaseArenaAnnouncementDtoSchema.refine(
  isValidValidityWindow,
  validityWindowError,
);
export type CreateArenaAnnouncementDto = z.infer<typeof CreateArenaAnnouncementDtoSchema>;

export const UpdateArenaAnnouncementDtoSchema = BaseArenaAnnouncementDtoSchema.extend({
  id: z.uuid(),
}).refine(isValidValidityWindow, validityWindowError);
export type UpdateArenaAnnouncementDto = z.infer<typeof UpdateArenaAnnouncementDtoSchema>;

export const SaveArenaAnnouncementDtoSchema = z.union([
  UpdateArenaAnnouncementDtoSchema,
  CreateArenaAnnouncementDtoSchema,
]);
export type SaveArenaAnnouncementDto = z.infer<typeof SaveArenaAnnouncementDtoSchema>;

// ====== Query params ======

export const GetArenaAnnouncementByIdQueryParamsSchema = z.object({
  arenaAnnouncementId: z.uuid(),
});
export type GetArenaAnnouncementByIdQueryParamsDto = z.infer<typeof GetArenaAnnouncementByIdQueryParamsSchema>;

export const GetArenaAnnouncementsByPageQueryParamsSchema = z.object({
  page: z.coerce.number().optional(),
  size: z.coerce.number().optional(),
  search: z.string().optional(),
});
export type GetArenaAnnouncementsByPageQueryParamsDto = z.infer<typeof GetArenaAnnouncementsByPageQueryParamsSchema>;

export const DeleteArenaAnnouncementQueryParamsSchema = z.object({
  arenaAnnouncementId: z.uuid(),
});
export type DeleteArenaAnnouncementQueryParamsDto = z.infer<typeof DeleteArenaAnnouncementQueryParamsSchema>;

// ====== Result schemas ======

export const GetArenaAnnouncementsByPageResultSchema = z.object({
  data: z.array(ArenaAnnouncementDtoSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
});
export type GetArenaAnnouncementsByPageResultDto = z.infer<typeof GetArenaAnnouncementsByPageResultSchema>;

export const GetPublishedArenaAnnouncementsResultSchema = z.array(ArenaAnnouncementDtoSchema);
export type GetPublishedArenaAnnouncementsResultDto = z.infer<typeof GetPublishedArenaAnnouncementsResultSchema>;
